import { Injectable, inject } from "@angular/core";
import { IFirmGroupsService } from "../shared";
import { ITranslateObject, JobsBillingCard } from "../views-interfaces";
import { MultiSelectModel, Option } from "@app/filter-menu/filter-types";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";

@Injectable()
export class IfirmGroupFilterGeneratorService {
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly translateService = inject(TranslateService);
    private currentLanguage = "en-CA";
    private noneText = "none";

    constructor() {
        this.currentLanguage = this.translateService.getCurrentLang()
            ? this.translateService.getCurrentLang()
            : this.translateService.getFallbackLang();
        this.noneText = this.currentLanguage === "fr-CA" ? "aucun" : "none";

        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang;
            this.noneText = this.currentLanguage === "fr-CA" ? "aucun" : "none";
        });
    }

    private sortGroupOptions<T extends { IsVisible: boolean }>(options: T[]): T[] {
        return options.sort((a, b) => {
            const languageValueA = (a as Record<string, unknown>)[this.currentLanguage];
            const languageValueB = (b as Record<string, unknown>)[this.currentLanguage];
            const textA = (typeof languageValueA === "string" ? languageValueA : "").toLowerCase();
            const textB = (typeof languageValueB === "string" ? languageValueB : "").toLowerCase();
            if (textA === this.noneText) return -1;
            if (textB === this.noneText) return 1;
            if (a.IsVisible !== b.IsVisible) return a.IsVisible ? -1 : 1;
            return textA.localeCompare(textB);
        });
    }

    async generateGroup1Model(selection: Option[]) {
        const iFirmGroups = await this.iFirmGroupsService.getiFirmGroups();
        const options: Option[] = this.sortGroupOptions(iFirmGroups.group1Options).map((option) => {
            return {
                text: {
                    "en-CA": option["en-CA"],
                    "fr-CA": option["fr-CA"],
                } as ITranslateObject,
                value: option.Id.toString(),
            } as Option;
        });
        return {
            label: {
                "en-CA": iFirmGroups.group1["en-CA"],
                "fr-CA": iFirmGroups.group1["fr-CA"],
            } as ITranslateObject,
            applicableOptions: options,
            allOptions: options,
            selection: selection.filter((selected) => options.some((applicable) => selected.value === applicable.value)),
        } as MultiSelectModel;
    }

    async generateGroup2Model(selection: Option[]) {
        const iFirmGroups = await this.iFirmGroupsService.getiFirmGroups();
        const options: Option[] = this.sortGroupOptions(iFirmGroups.group2Options).map((option) => {
            return {
                text: {
                    "en-CA": option["en-CA"],
                    "fr-CA": option["fr-CA"],
                } as ITranslateObject,
                value: option.Id.toString(),
            } as Option;
        });
        return {
            label: {
                "en-CA": iFirmGroups.group2["en-CA"],
                "fr-CA": iFirmGroups.group2["fr-CA"],
            } as ITranslateObject,
            applicableOptions: options,
            allOptions: options,
            selection: selection.filter((selected) => options.some((applicable) => selected.value === applicable.value)),
        } as MultiSelectModel;
    }

    getPrintDescription(
        label: string | ITranslateObject,
        selectedOptions: Option[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedOptions || selectedOptions.length === 0) {
            return null;
        } else {
            return {
                label,
                values: selectedOptions.map((value) => {
                    return value.text;
                }),
            };
        }
    }

    async generateGroup1Filter(selectedValues: string[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            return (card: JobsBillingCard) => {
                const group1 = card.contactInformation.group1;
                return selectedValues.includes(group1) || (group1 == null && selectedValues.includes("-1"));
            };
        }
    }

    async generateGroup2Filter(selectedValues: string[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            return (card: JobsBillingCard) => {
                const group2 = card.contactInformation.group2;
                return selectedValues.includes(group2) || (group2 == null && selectedValues.includes("-1"));
            };
        }
    }
}
